import React, { useState } from 'react';
import { 
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface MultiAngleCarouselProps {
  images: { [angle: string]: string };
  userImage: string;
  className?: string;
}

const MultiAngleCarousel: React.FC<MultiAngleCarouselProps> = ({
  images,
  userImage,
  className = ""
}) => {
  const angles = ['front', 'left', 'back', 'right'] as const;
  const angleLabels: Record<string, string> = {
    front: 'Front View',
    left: 'Left Side',
    back: 'Back View',
    right: 'Right Side'
  };
  
  // Filter to only show angles that have images
  const availableAngles = angles.filter(angle => images[angle]);
  const [currentAngleIndex, setCurrentAngleIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(false);

  // Helper to ensure image source is a valid data URI
  const formatImageSrc = (src: string): string => {
    if (!src || src.startsWith('data:image') || src.startsWith('http')) {
      return src;
    }
    // Assuming PNG format for raw base64 strings
    return `data:image/png;base64,${src}`;
  };

  const currentAngle = availableAngles[currentAngleIndex];
  const currentImage = images[currentAngle];

  React.useEffect(() => {
    if (!isAutoRotating) return;

    const interval = setInterval(() => {
      setCurrentAngleIndex(prev => (prev + 1) % availableAngles.length);
    }, 2000); // Change every 2 seconds

    return () => clearInterval(interval);
  }, [isAutoRotating, availableAngles.length]);

  const goToNext = () => {
    setIsAutoRotating(false);
    setCurrentAngleIndex((prev) => (prev + 1) % availableAngles.length);
  };

  const goToPrevious = () => {
    setIsAutoRotating(false);
    setCurrentAngleIndex((prev) => (prev - 1 + availableAngles.length) % availableAngles.length);
  };

  const selectAngle = (index: number) => {
    setIsAutoRotating(false);
    setCurrentAngleIndex(index);
  };

  const toggleAutoRotate = () => {
    setIsAutoRotating(!isAutoRotating);
  };

  if (availableAngles.length === 0) {
    return (
      <div className={`text-center p-8 ${className}`}>
        <p className="text-gray-400">No comparison images available</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Side-by-Side Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-purple-900/30 rounded-xl border border-purple-700">
        {/* User's Image */}
        <div>
          <h4 className="font-medium text-white mb-3">Your Image</h4>
          <img 
            src={formatImageSrc(userImage)} 
            alt="Your uploaded item" 
            className="w-full h-64 object-contain bg-gray-700 rounded-lg border border-gray-600"
          />
        </div>

        {/* AI Multi-Angle Carousel */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-white">AI Generated Views</h4>
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleAutoRotate}
                className={`p-1.5 rounded transition-colors ${
                  isAutoRotating 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                }`}
                title="Auto Rotate"
              >
                <ArrowPathIcon className="h-4 w-4" />
              </button>
              <span className="text-xs bg-purple-600 px-2 py-1 rounded-full text-white">
                {availableAngles.length} Views
              </span>
            </div>
          </div>

          {/* Main Image Display */}
          <div className="relative">
            <img 
              src={formatImageSrc(currentImage)} 
              alt={`AI generated ${angleLabels[currentAngle]}`}
              className="w-full h-64 object-contain bg-gray-700 rounded-lg border border-gray-600 transition-opacity duration-300"
            />

            {/* Navigation Buttons */}
            {availableAngles.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  title="Previous View"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  title="Next View"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </>
            )}

            {/* Current View Label */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
              {angleLabels[currentAngle]}
            </div>

            {/* Auto-rotate indicator */}
            {isAutoRotating && (
              <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded text-xs">
                Auto
              </div>
            )}
          </div>

          {/* Angle Selector Dots */}
          {availableAngles.length > 1 && (
            <div className="flex justify-center mt-4 space-x-2">
              {availableAngles.map((angle, index) => (
                <button
                  key={angle}
                  onClick={() => selectAngle(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentAngleIndex
                      ? 'bg-purple-500'
                      : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                  title={angleLabels[angle]}
                />
              ))}
            </div>
          )}

          <p className="text-sm text-purple-400 mt-2">
            Generated using Google's Imagen AI • {availableAngles.length} angle{availableAngles.length > 1 ? 's' : ''} available
          </p>
        </div>
      </div>
    </div>
  );
};

export default MultiAngleCarousel;
